import TR from 'utils/TR';
import T from 'utils/T';
import ex from 'utils/ex';
import VBox from 'components/common/VBox';
import HBox from 'components/common/HBox';
import FocusableButton from 'components/common/FocusableButton';
import Input from 'components/common/Input';
import SelectButton from 'components/common/SelectButton';
import React from 'react';
import Recoil from 'recoil';
import * as AppStore from 'stores/AppStore';
import useForm, { defaultPasswordValidatingOptions } from 'hooks/common/useForm';
import useDoctorPwdChg from 'hooks/communication/useDoctorPwdChg';
import useMessageBox from 'hooks/app/useMessageBox';
import useDoctorLogout from 'hooks/communication/useDoctorLogout';
import useLogout from 'hooks/app/useLogout';

export default function ResetDialog(props) {
  const {
    index,
    count,
    onPrev,
    onNext,
    events,
    ...rest
  } = props;

  const {
    data,
    errors,
    register,
    validate,
    onSubmit,
  } = useForm({
    defaultValues: {
      current: '',
      password: '',
      confirm: '',
    },
  });

  const {
    onDoctorPwdChg,
  } = useDoctorPwdChg();

  const {
    open,
    update,
    close,
  } = useMessageBox('App');

  const {
    requestDoctorLogout,
  } = useDoctorLogout();

  const onLogout = useLogout();

  const onPasswordChanged = React.useCallback((success, data, parameters) => {
    events?.onClose();
    if (success) {
      open({
        id: 'password',
        title: T('비밀번호 변경 성공'),
        contents: (
          <div>
            {T('비밀번호가 변경되어 로그아웃 합니다.')}
          </div>
        ),
        buttons: [ex('primary', {
          autoFocus: true,
          caption: T('확인'),
          event: 'onClose',
        })],
        events: {
          onClose: () => {
            close('password');
            requestDoctorLogout({ dr_id }, onLogout);
          },
        },
      });
    } else {
      open({
        id: 'password',
        title: T('비밀번호 변경 실패'),
        contents: (
          <div>
            {data.message}
          </div>
        ),
        buttons: [ex('primary', {
          autoFocus: true,
          caption: T('확인'),
          event: 'onClose',
        })],
        events: {
          onClose: () => close('password'),
        },
      });
    }
  }, [events]);

  const onChange = React.useCallback((id) => () => {
    validate(id);
  }, [validate]);

  const doctor_info = Recoil.useRecoilValue(AppStore.assets.doctor_info);
  const {
    dr_id,
  } = doctor_info || {};

  return (
    <VBox
      {...ex(['Dialog', 'ResetDialog'], rest)}
    >
      <HBox {...ex('DialogTitle')}>
        <div>
          {T('패스워드 재설정')}
        </div>
        <div>
          <FocusableButton
            {...{
              onClick: events?.onClose,
            }}
          >
            <svg width={24} height={24} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fillRule="evenodd" clipRule="evenodd" d="M6.29279 6.29308C6.48031 6.10561 6.73462 6.00029 6.99979 6.00029C7.26495 6.00029 7.51926 6.10561 7.70679 6.29308L11.9998 10.5861L16.2928 6.29308C16.385 6.19757 16.4954 6.12139 16.6174 6.06898C16.7394 6.01657 16.8706 5.98898 17.0034 5.98783C17.1362 5.98668 17.2678 6.01198 17.3907 6.06226C17.5136 6.11254 17.6253 6.18679 17.7192 6.28069C17.8131 6.37458 17.8873 6.48623 17.9376 6.60913C17.9879 6.73202 18.0132 6.8637 18.012 6.99648C18.0109 7.12926 17.9833 7.26048 17.9309 7.38249C17.8785 7.50449 17.8023 7.61483 17.7068 7.70708L13.4138 12.0001L17.7068 16.2931C17.8889 16.4817 17.9897 16.7343 17.9875 16.9965C17.9852 17.2587 17.88 17.5095 17.6946 17.6949C17.5092 17.8803 17.2584 17.9855 16.9962 17.9878C16.734 17.99 16.4814 17.8892 16.2928 17.7071L11.9998 13.4141L7.70679 17.7071C7.51818 17.8892 7.26558 17.99 7.00339 17.9878C6.74119 17.9855 6.49038 17.8803 6.30497 17.6949C6.11956 17.5095 6.01439 17.2587 6.01211 16.9965C6.00983 16.7343 6.11063 16.4817 6.29279 16.2931L10.5858 12.0001L6.29279 7.70708C6.10532 7.51955 6 7.26525 6 7.00008C6 6.73492 6.10532 6.48061 6.29279 6.29308Z" fill="#5B6871" />
            </svg>
          </FocusableButton>
        </div>
      </HBox>
      <form {...{ onSubmit: onSubmit(onDoctorPwdChg({ dr_id }, onPasswordChanged)) }}>
        <VBox>
          <div>
            {T('현재 패스워드 입력')}
          </div>
          <Input
            {...register('current', {
              type: 'password',
              autoComplete: 'current-password',
              placeholder: T('비밀번호를 입력하세요.'),
              validate: defaultPasswordValidatingOptions(['admin', 'root']),
            })}
            {...ex({ error: errors['current'] })}
          >
            {errors['current'] && (
              <div {...ex('ErrorMessage')}>
                {errors['current']}
              </div>
            )}
          </Input>
        </VBox>
        <VBox>
          <div>
            {T('신규 패스워드 입력')}
          </div>
          <Input
            {...register('password', {
              type: 'password',
              autoComplete: 'new-password',
              placeholder: T('비밀번호를 입력하세요.'),
              validate: defaultPasswordValidatingOptions(['admin', 'root', data['current']]),
              onChange: onChange('confirm'),
            })}
            {...ex({ error: errors['password'] })}
          >
            {errors['password'] && (
              <div {...ex('ErrorMessage')}>
                {errors['password']}
              </div>
            )}
          </Input>
        </VBox>
        <VBox>
          <div>
            {T('신규 패스워드 재입력')}
          </div>
          <Input
            {...register('confirm', {
              type: 'password',
              autoComplete: 'new-password',
              placeholder: T('비밀번호를 입력하세요.'),
              validate: ({ password, confirm }) => !(confirm && confirm !== password) ? null : T('비밀번호가 일치하지 않습니다.'),
              onChange: onChange('password'),
            })}
            {...ex({ error: errors['confirm'] })}
          >
            {errors['confirm'] && (
              <div {...ex('ErrorMessage')}>
                {errors['confirm']}
              </div>
            )}
          </Input>
        </VBox>
        <FocusableButton
          {...ex('SendButton', {
            type: 'submit',
          })}
        >
          {T('비밀번호 변경')}
        </FocusableButton>
      </form>
    </VBox >
  );
}
